<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="90%" max-height="90%" style="min-height: 80% !important;">
      <v-skeleton-loader v-if="loading" type="article, actions"></v-skeleton-loader>
      <v-card v-if="!loading" style="min-height:570px; max-height: 570px;">
        <v-card-title>
          <v-panel>
              <span style="font-size: 20px">
                {{ $t("wkt.admin.projects.project.edit_title") }}
              </span>
          </v-panel>
        </v-card-title>
        <v-card-text style="padding-bottom: 0">
          <v-container fluid @wheel="onWheel">
              <v-tabs v-model="tab" v-if="!loading" centered>
                <v-tab href="#pt-1">{{ $t("wkt.admin.projects.project.tab1") }}</v-tab>
                <v-tab href="#pt-2">{{ $t("wkt.admin.projects.project.tab2") }}</v-tab>
                <v-tab v-if="data.projectType==0" href="#pt-3">{{ $t("wkt.admin.projects.project.tab3") }}</v-tab>
                <v-tab href="#pt-4">{{ $t("wkt.admin.projects.project.tab4") }}</v-tab>
              </v-tabs>
              <v-tabs-items  v-if="!loading" v-model="tab">
                  <v-tab-item eager value="pt-1">
                    <v-form ref="form">
                      <v-row style="margin-top: 5px" align="center">
                        <v-col cols="3" class="d-flex align-center">
                          <div
                            :style="{
                              backgroundColor: convertColorToHex(data.color),
                              width: '22px',
                              height: '22px',
                              cursor: 'pointer',
                              border: '1px solid grey',
                              marginRight: '10px',
                            }"
                            @click="openColorDialog()"
                          />
                          <v-text-field
                            v-model="data.shortName"
                            :label="$t('wkt.admin.projects.project.short_name')"
                            autocomplete="none"
                            :rules="[validationRules.required]"
                            maxLength="20"
                            counter="20" />
                        </v-col>
                        <v-col cols="3" class="d-flex align-center">
                          <v-menu
                            ref="fromDateMenu"
                            v-model="fromDateMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            max-width="290px"
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="fromDateFormatted"
                                :label="$t('wkt.admin.projects.calendar-wizard.start_date_placeholder')"
                                v-on="on"
                                v-bind="attrs"
                                @blur="fromDate = formatLocalDateToISOStringDate(fromDateFormatted)"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="fromDate"
                              :first-day-of-week="1" 
                              :locale="$i18n.locale"
                              no-title
                              @input="updateFormattedDate()"
                              @change="fromDateMenu = false"
                            />
                          </v-menu>
                        </v-col>
                        <v-col cols="3" class="d-flex align-center">
                          <v-menu
                            ref="toDateMenu"
                            v-model="toDateMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            max-width="290px"
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="toDateFormatted"
                                :label= "$t('wkt.admin.projects.calendar-wizard.end_date_placeholder')"
                                :error-messages="errorMessages"
                                v-on="on"
                                v-bind="attrs"
                                @blur="toDate = formatLocalDateToISOStringDate(toDateFormatted)"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="toDate"
                              :first-day-of-week="1" 
                              :locale="$i18n.locale"
                              no-title
                              @input="updateFormattedDate()"
                              @change="toDateMenu = false"
                            />
                          </v-menu>
                        </v-col>
                        <v-col cols="3">
                          <v-text-field
                            v-model="data.internalCode"
                            :label="$t('wkt.admin.teams.team.internal-code')"
                            maxLength="20"
                            counter="20" />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="9">
                          <v-text-field
                            v-model="data.description"
                            :label="$t('wkt.admin.projects.project.description')"
                            autocomplete="none"
                            :rules="[validationRules.required]"
                            maxLength="250"
                            counter="250" />
                        </v-col>
                        <v-col cols="3" class="d-flex justify-start">
                              <v-checkbox
                                v-model="data.enabled"
                                :label="$t('wkt.admin.projects.project.enabled')" />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12">
                          <v-autocomplete
                            v-model="managerIds"
                            chips
                            multiple
                            clearable
                            deletable-chips
                            :items="managers"
                            :item-text="item => `${item.lastname}, ${item.firstname}${!item.isEnabled ? ` (${this.$t('wkt.admin.edit-employee-window.disabled')})` : ''}`"
                            item-value="id"
                            :filter="filterManagers"
                            menu-props="bottom, offsetY"
                            :label="$t('wkt.admin.projects.project.managers')"
                            :placeholder="$t('wkt.admin.common.filters.employee_placeholder')" />
                        </v-col>
                      </v-row>
                    </v-form>
                </v-tab-item>
                <v-tab-item eager value="pt-2">
                    <EmployeesManager 
                      ref="employeeManager"
                      :projectType="data.projectType"
                      :tableLoading="tableLoading"
                      :tableHeaders="tableHeaders"
                      :tableItems="employees" 
                      :emptyStateTitle="$t('wkt.admin.teams.team.empty-state.title')" 
                      :emptyStateDescription="$t('wkt.admin.teams.team.empty-state.description')" 
                      tableHeight="255px"
                      @remove-all-employees="confirmRemoveAllProjectEmployees($event)" 
                      @add-employees="addProjectEmployees()" 
                      @edit-employee="editProjectEmployee($event)"
                      @remove-employee="removeProjectEmployee($event)" 
                    />
                </v-tab-item>
                <v-tab-item eager value="pt-3">
                    <v-form ref="portfolioForm">
                      <v-row style="margin-top: 5px" align="center">
                        <v-col cols="12">
                          <span style="font-size:18px; font-weight:500; color:black">
                            {{ portfolioSectionTitle }}
                          </span>
                        </v-col>
                        <v-col cols="3" class="d-flex align-center">
                          <v-menu
                            ref="plannedStartDateMenu"
                            v-model="plannedStartDateMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            max-width="290px"
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                :disabled="!enabledPortfolioFields"
                                v-model="plannedStartDateFormatted"
                                :label="$t('wkt.admin.projects.project.planned_start_date')"
                                v-on="on"
                                v-bind="attrs"
                                @blur="plannedStartDate = formatLocalDateToISOStringDate(plannedStartDateFormatted)"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="plannedStartDate"
                              :first-day-of-week="1" 
                              :locale="$i18n.locale"
                              no-title
                              @input="updateFormattedDate()"
                              @change="plannedStartDateMenu = false"
                            />
                          </v-menu>
                        </v-col>
                        <v-col cols="3" class="d-flex align-center">
                          <v-menu
                            ref="plannedEndDateMenu"
                            v-model="plannedEndDateMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            max-width="290px"
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                :disabled="!enabledPortfolioFields"
                                v-model="plannedEndDateFormatted"
                                :label= "$t('wkt.admin.projects.project.planned_end_date')"
                                :error-messages="errorMessages"
                                v-on="on"
                                v-bind="attrs"
                                @blur="plannedEndDate = formatLocalDateToISOStringDate(plannedEndDateFormatted)"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="plannedEndDate"
                              :first-day-of-week="1" 
                              :locale="$i18n.locale"
                              no-title
                              @input="updateFormattedDate()"
                              @change="plannedEndDateMenu = false"
                            />
                          </v-menu>
                        </v-col>
                        <v-col cols="3">
                          <v-text-field
                            :disabled="!enabledPortfolioFields"
                            v-model="formattedProgress"
                            type="number"
                            :min="0"
                            :max="100"
                            :step="0.01"
                            :label="$t('wkt.admin.projects.project.progress')"
                            autocomplete="none"
                            suffix="%"/>
                             </v-col>
                        <v-col cols="3" class="d-flex justify-start">
                              <v-checkbox
                              :disabled="!enabledPortfolioFields"
                                v-model="data.includeInPortfolio" 
                                :label="$t('wkt.admin.projects.project.include_in_portfolio')" />
                        </v-col>
                         <v-col cols="3">
                          <v-text-field
                            :disabled="!enabledPortfolioFields"
                            v-model="data.plannedIncome"
                            :label="$t('wkt.admin.projects.project.planned_income')"
                            type="number"
                            :min="0"
                            :max="9999999999"
                            autocomplete="none"/>
                        </v-col>
                         <v-col cols="3">
                          <v-text-field
                            :disabled="!enabledPortfolioFields"
                            v-model="data.actualOtherCost"
                            :label="$t('wkt.admin.projects.project.actual_other_cost')"
                            type="number"
                            :min="0"
                            :max="9999999999"
                            autocomplete="none"/>
                        </v-col>
                         <v-col cols="3">
                              <v-select
                                :disabled="!enabledPortfolioFields"
                                v-model="data.projectStatus"
                                :items="projectStatuses"
                                item-text="name"
                                item-value="id"
                                :label="$t('wkt.admin.projects.project.project_status')"
                                menu-props="bottom, offsetY" />
                            </v-col>
                        <v-col cols="12">
                          <span style="font-size:18px; font-weight:500; color:black">
                            {{ budgetSectionTitle }}
                          </span>
                        </v-col>
                         <v-col cols="3">
                          <v-text-field
                            :disabled="!enableBudgetFields"
                            v-model="data.budgetHours"
                            :label="$t('wkt.admin.projects.project.budget_hours')"
                            type="number"
                            :min="0"
                            :max="9999999999"
                            autocomplete="none"/>
                        </v-col>
                        <v-col cols="3">
                          <v-text-field
                            :disabled="!enableBudgetFields"
                            v-model="data.budgetHoursCost"
                            :label="$t('wkt.admin.projects.project.budget_hours_cost')"
                            type="number"
                            :min="0"
                            :max="9999999999"
                            autocomplete="none"/>
                        </v-col>
                        <v-col cols="3">
                          <v-text-field
                            :disabled="!enableBudgetFields"
                            v-model="data.budgetExpenses"
                            :label="$t('wkt.admin.projects.project.budget_expenses')"
                            type="number"
                            :min="0"
                            :max="9999999999"
                            autocomplete="none"/>
                        </v-col>
                        <v-col cols="3">
                          <v-text-field
                            :disabled="!enableBudgetFields"
                            v-model="data.budgetOtherCost"
                            :label="$t('wkt.admin.projects.project.budget_other_cost')"
                            type="number"
                            :min="0"
                            :max="9999999999"
                            autocomplete="none"/>
                        </v-col>
                      </v-row>
                    </v-form>
                </v-tab-item>
                <v-tab-item eager value="pt-4">
                  <v-form ref="publicDataForm">
                    <v-row style="margin-top: 5px">
                      <v-col cols="6">
                        <v-text-field
                          v-model="data.client"
                          :label="$t('wkt.admin.projects.project.public_data.client')"
                          maxlength="100"
                          counter="100"
                        />
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model="data.contactPerson"
                          :label="$t('wkt.admin.projects.project.public_data.contact_person')"
                          maxlength="100"
                          counter="100"
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-text-field
                          v-model="data.address"
                          :label="$t('wkt.admin.projects.project.public_data.address')"
                          maxlength="200"
                          counter="200"
                        />
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model="data.contactPhone"
                          :label="$t('wkt.admin.projects.project.public_data.contact_phone')"
                          maxlength="20"
                          counter="20"
                        />
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model="data.contactEmail"
                          :label="$t('wkt.admin.projects.project.public_data.contact_email')"
                          type="email"
                          maxlength="100"
                          counter="100"
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="data.observations"
                          :label="$t('wkt.admin.projects.project.public_data.observations')"
                          rows="2"
                          maxlength="500"
                          counter="500"
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="data.specialRequirements"
                          :label="$t('wkt.admin.projects.project.public_data.special_requirements')"
                          rows="2"
                          maxlength="500"
                          counter="500"
                        />
                      </v-col>
                    </v-row>
                  </v-form>
                </v-tab-item>
              </v-tabs-items>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn v-if="showDeleteButton" color="#ff0000" @click="confirmDelete()">
            <v-icon left color="#ffffff">delete</v-icon>
            <span style="color: #ffffff;">
              {{ $t('wkt.admin.common.dialog-buttons.delete').toUpperCase() }}
            </span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="#185EA9" text @click="onSave()" style="font-size: 0.9em; font-weight: 400">
            {{ $t("wkt.admin.common.dialog-buttons.save").toUpperCase() }}
          </v-btn>
          <v-btn text @click="onCancel()" style="font-size: 0.9em; font-weight: 400">
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ColorDialog ref='dlgColor' />

    <TargetManagerDialog 
      ref="dlgTargetManager"
      :filterByProfile="true"
      :title="$t('wkt.admin.employee-selector-window.title_project')" 
      :subtitle="`${data.shortName} - ${data.description}`" 
      :targetLabel="$t('wkt.admin.employee-selector-window.target_placeholder')" 
    />

    <EditProjectEmployeeDialog ref="dlgEditEmployee" />

    <ConfirmDialog
      ref='dlgConfirmRemoveAllProjectEmployees'
      :title="$t('wkt.admin.teams.team.remove-employees-confirm-dialog.title')"
      :message="confirmMessage"
      :titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      :titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="removeAllProjectEmployees()"
    />

    <ConfirmDialog
      ref='dlgConfirm'
      :title="$t('wkt.admin.projects.project.confirm-dialog.title')"
      :message="$t('wkt.admin.projects.project.confirm-dialog.text')"
      :titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      :titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="deleteItem()"
    />
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import ColorDialog from "../../common/ColorDialog.vue";
import EmployeesManager from "../../common/EmployeesManager.vue";
import EditProjectEmployeeDialog from "./EditProjectEmployeeDialog.vue";
import ConfirmDialog from "../../common/ConfirmDialog.vue";
import TargetManagerDialog from "./TargetManagerDialog.vue";
import api from "../../../api/worktime.js";
import gwt from "../../../api/gwt.js";
import CommonFormatter from "../../common/CommonFormatter.js"


export default {
  name: "EditProjectDialog",
  mixins: [BaseModule, CommonFormatter],
  components: {
    ColorDialog, EmployeesManager, EditProjectEmployeeDialog, ConfirmDialog, TargetManagerDialog
  },
  data() {
    return {
      showDialog: false,
      loading: false,
      confirmMessage: '',
      data: {},
      employees: [],
      tableLoading: false,
      tableHeaders: [],
      showDeleteButton: false,
      fromDate: null,
      toDate: null,
      fromDateFormatted: null,
      toDateFormatted: null,
      plannedStartDate: null,
      plannedEndDate: null,
      plannedStartDateFormatted: null,
      plannedEndDateFormatted: null,
      fromDateMenu: false,
      plannedStartDateMenu: false,
      toDateMenu: false,
      plannedEndDateMenu: false,
      managers: [],
      managerIds: [],
			errorMessages: '',
      dataHasChanged: false,
      formattedProgress: 0,
      tab: '#pt-1',
      enabledPortfolioFields: false,
      portfolioSectionTitle: null,
      enableBudgetFields: false,
      budgetSectionTitle: null,
      projectStatuses: [ {id: 0, name: this.$t('wkt.admin.projects.project.project_statuses.not_started')},
                        {id: 1, name: this.$t('wkt.admin.projects.project.project_statuses.on_track')},
                        {id: 2, name: this.$t('wkt.admin.projects.project.project_statuses.at_risk')},
                        {id: 3, name: this.$t('wkt.admin.projects.project.project_statuses.off_track')},
                        {id: 4, name: this.$t('wkt.admin.projects.project.project_statuses.completed')}]
    };
  },
  methods: {
    onWheel(event) {
      event.stopPropagation()
    },
    open(action, payload) {
			this.errorMessages = '';
      this.showDialog = true;
      this.dataHasChanged = false;
      this.enabledPortfolioFields = this.getCurrentCompany().licenseType >= this.constants.LICENSE_TYPE_BUSINESS;
      this.portfolioSectionTitle = this.enabledPortfolioFields ? this.$t('wkt.admin.projects.project.portfolio_section_title') : this.$t('wkt.admin.projects.project.portfolio_section_title_limited');
      this.enableBudgetFields = this.getCurrentCompany().licenseType >= this.constants.LICENSE_TYPE_CORPORATE;
      this.budgetSectionTitle = this.enableBudgetFields ? this.$t('wkt.admin.projects.project.budget_section_title') : this.$t('wkt.admin.projects.project.budget_section_title_limited');
      this.tab = '#pt-1';
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
      
      this.tableHeaders = [
        { text: this.$t('wkt.admin.projects.project.table.headers.name'), value: 'firstname', width: '25%', sortable: false },
        { text: this.$t('wkt.admin.projects.project.table.headers.id_number'), value: 'idNumber', width: '15%', sortable: false },
        { text: this.$t('wkt.admin.projects.project.table.headers.workplace'), value: 'workplaceName', width: '15%', sortable: false },
        { text: this.$t('wkt.admin.projects.project.table.headers.department'), value: 'departmentName', width: '15%', sortable: false },
        { text: this.$t('wkt.admin.projects.project.table.headers.dedication'), value: 'dedication', width: '10%', sortable: false },
        { text: '', value: 'editAction', width: '10%', sortable: false },
        { text: '', value: 'removeAction', width: '10%', sortable: false },
      ];

      this.loading = true;

      this.showDeleteButton = this.data != null;
      api.getProjectById(payload,
        (res) => {
          this.data = res;
          this.loading = false;

          this.loadFromDate(this.convertSimpleDateToDate(this.data.fromDate));
          this.loadToDate(this.convertSimpleDateToDate(this.data.toDate));

          this.plannedStartDate = this.data.plannedStartDate ? this.formatLocalDateToISOStringDate(this.convertSimpleDateToDate(this.data.plannedStartDate)).split(', ')[0] : null;
          this.plannedStartDateFormatted = this.data.plannedStartDate ? this.formatOnlyDate(this.plannedStartDate) : null;

          this.plannedEndDate = this.data.plannedEndDate ? this.formatLocalDateToISOStringDate(this.convertSimpleDateToDate(this.data.plannedEndDate)).split(', ')[0] : null;
          this.plannedEndDateFormatted = this.data.plannedEndDate ? this.formatOnlyDate(this.plannedEndDate) : null;

          this.formattedProgress = this.data.progress ? (this.data.progress * 100).toFixed(2) : 0;

          this.getDepartments();
          this.getProjectEmployeesByProjectId(this.data.id);
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
		getDepartments() {
			if (gwt.getDepartments()) {
        this.departments = gwt.getDepartments();
				this.getAllManagers();
        this.managerIds = this.data.managerIds ? this.data.managerIds.split(',').map(Number) : [];
				return;
			}

			api.getStaff(
				(data) => {
					this.departments = data;
					this.getAllManagers();
          this.managerIds = this.data.managerIds ? this.data.managerIds.split(',').map(Number) : [];
				}, 
				(error) => {
					this.loading = false;
					this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
				}
			);
		},
		getAllManagers() {
			this.managers = [];

			for (let i = 0; i < this.departments.length; i++) {
				this.managers = [...this.managers, ...this.departments[i].employees];
			}

			this.managers.sort((a,b) => {
				const fullnameA = this.getEmployeeFullname(a).toLowerCase();
				const fullnameB = this.getEmployeeFullname(b).toLowerCase();
				if (fullnameA == fullnameB) return 0;
				if (fullnameA > fullnameB) return 1;
				return -1;
			});
		},
		// eslint-disable-next-line no-unused-vars
		filterManagers(item, queryText, itemText) {
      const normalizedFirstname = item.firstname.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
			const normalizedLastname = item.lastname.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const normalizedQueryText = queryText.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

      return normalizedFirstname.indexOf(normalizedQueryText) > -1 || 
        normalizedLastname.indexOf(normalizedQueryText) > -1;
    },
    openColorDialog() {
      this.$refs.dlgColor.open(this.data.color, (color) => {
        this.data.color = color;
      });
    },
    getProjectEmployeesByProjectId(projectId) {
      this.tableLoading = true;

      api.getFullProjectsEmployeesByProjectId(projectId,
        (res) => {
          this.tableLoading = false;
          this.employees = res;
          this.employees.forEach((employee) => {
           employee.dedication = this.formatCurrency(employee.dedication);
        });
        },
        (error) => {
          this.tableLoading = false;
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    confirmRemoveAllProjectEmployees(numEmployeesToRemove) {
      this.confirmMessage = this.$t('wkt.admin.projects.project.remove-employees-confirm-dialog.text');
      this.confirmMessage = this.confirmMessage.replace('%d', `${numEmployeesToRemove}`);
      
      this.$refs.dlgConfirmRemoveAllProjectEmployees.confirm(this.data);
    },
    removeAllProjectEmployees() {
      const data = {
        projectEmployeeIds: this.$refs.employeeManager.getIds(true)
      };

      this.tableLoading = true;

      api.deleteProjectEmployeesByProjectList(data,
        () => {
          this.tableLoading = false;
          this.getProjectEmployeesByProjectId(this.data.id);
          this.dataHasChanged = true;
        },
        (error) => {
          this.tableLoading = false;
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    addProjectEmployees() {
      this.$refs.dlgTargetManager.open(this.$refs.employeeManager.getEmployeeIds(false), this.data.id, (employeeIds) => {
        const alreadyAddedEmployeeIds = this.employees.map(e => e.employeeId);
        const selectedEmployeeIds = employeeIds.filter(id => !alreadyAddedEmployeeIds.includes(id))

        const data = {
          projectId: this.data.id,
          employeeIds: selectedEmployeeIds
        };

        this.tableLoading = true;

        api.addProjectEmployees(data,
          () => {
            this.tableLoading = false;
            this.getProjectEmployeesByProjectId(this.data.id);
            let message = this.$t('wkt.admin.projects.project.num_employees_added');
            message = message.replace('%d', `${selectedEmployeeIds.length}`);
            this.$emit("show-alert", { text: message, color: "success", timeout: 3000 });
            this.dataHasChanged = true;
          },
          (error) => {
            this.tableLoading = false;
            this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
          }
        );
      });
    },
    editProjectEmployee(employeeId) {
      const employee = this.employees.find(x => x.id === employeeId);

      this.$refs.dlgEditEmployee.open(employee, (dedication) => {
        const data = {
          id: employee.id,
          projectId: employee.projectId,
          employeeId: employee.employeeId,
          dedication: dedication
        };

        this.tableLoading = true;

        api.storeProjectEmployee(data,
          (res) => {
            this.tableLoading = false;
            this.getProjectEmployeesByProjectId(this.data.id);
            if (res.message) {
              this.$emit("show-alert", { text: res.message, color: "success", timeout: 3000 });
            }
            this.dataHasChanged = true;
          },
          (error) => {
            this.tableLoading = false;
            this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
          }
        );
      });
    },
    removeProjectEmployee(employeeId) {
      this.tableLoading = true;

      api.deleteProjectEmployeeById(employeeId,
        () => {
          this.tableLoading = false;
          this.getProjectEmployeesByProjectId(this.data.id);
          this.dataHasChanged = true;
        },
        (error) => {
          this.tableLoading = false;
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    onSave() {
      if (!this.$refs.form.validate() || !this.$refs.portfolioForm.validate() || !this.$refs.publicDataForm.validate()) {
        return;
      }
      
      this.errorMessages = '';

			if (this.fromDate && this.toDate) {
        const dtStart = new Date(`${this.fromDate}`).getTime();
        const dtEnd = new Date(`${this.toDate}`).getTime();

        if (dtStart > dtEnd) {
          this.errorMessages = this.$t('wkt.admin.holidays.calendar.edit-holiday-window.new-record-window.errors.end_date_smaller');
          return;
        }
      }

      if (this.plannedStartDate && this.plannedEndDate) {
        const dtPlannedStart = new Date(`${this.plannedStartDate}`).getTime();
        const dtPlannedEnd = new Date(`${this.plannedEndDate}`).getTime();

        if (dtPlannedStart > dtPlannedEnd) {
          this.errorMessages = this.$t('wkt.admin.projects.project.edit_window.errors.planned_end_date_smaller');
          return;
        }
      }

      this.data.fromDate = this.convertDateToSimpleDate(this.fromDate ? new Date(`${this.fromDate}`) : null);
      this.data.toDate = this.convertDateToSimpleDate(this.toDate ? new Date(`${this.toDate}`) : null);
      this.data.plannedStartDate = this.convertDateToSimpleDate(this.plannedStartDate ? new Date(`${this.plannedStartDate}`) : null);
      this.data.plannedEndDate = this.convertDateToSimpleDate(this.plannedEndDate ? new Date(`${this.plannedEndDate}`) : null);
      this.data.managerIds = this.managerIds.length > 0 ? this.managerIds.toString() : null;

      this.data.progress = this.formattedProgress ? parseFloat(this.formattedProgress)/100 : 0;
      this.loading = true;

      api.storeProject(this.data,
        (res) => {
          this.loading = false;
          if (res) {
            this.$emit("refresh");
            this.showDialog = false;
          } else {
            this.loading = false;
            this.$emit("show-alert", { text: res.message, color: "error", timeout: 3000 });
          }
        },
        (error) => {
          this.loading = false;
          this.$log.error(error);
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    confirmDelete() {
      this.$refs.dlgConfirm.confirm(this.data);
    },
    deleteItem() {
      this.loading = true;

      api.deleteProjectById(this.data.id,
        () => {
          this.loading = false;
          this.$emit("refresh");
          this.showDialog = false;
        },
        (error) => {
          this.loading = false;
          this.$log.error(error);
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    updateFormattedDate() {
      if (this.fromDate) {
        this.fromDateFormatted = this.formatISOStringDateToLocalDate(this.fromDate);
      }
      if (this.toDate) {
        this.toDateFormatted = this.formatISOStringDateToLocalDate(this.toDate);
      }
      if (this.plannedStartDate) {
        this.plannedStartDateFormatted = this.formatISOStringDateToLocalDate(this.plannedStartDate);
      }
      if (this.plannedEndDate) {
        this.plannedEndDateFormatted = this.formatISOStringDateToLocalDate(this.plannedEndDate);
      }
    },
    loadFromDate(date) {
      const isEmpty = date ? false : true;

      if (!date) {
        date = new Date();
        date.setHours(12);
        date.setMinutes(0);
        date.setSeconds(0);
      }// else {
        date = this.formatLongDateTime(date);
      //}

      if (!isEmpty) {
        this.fromDate = this.formatLocalDateToISOStringDate(date.split(', ')[0]);
        this.fromDateFormatted = this.formatISOStringDateToLocalDate(this.fromDate);
      } else {        
        this.fromDate = null;
        this.fromDateFormatted = null;
      }
    },
    loadToDate(date) {
      const isEmpty = date ? false : true;

      if (!date) {
        date = new Date();
        date.setHours(12);
        date.setMinutes(0);
        date.setSeconds(0);
      }// else {
        date = this.formatLongDateTime(date);
      //}

      if (!isEmpty) {
        this.toDate = this.formatLocalDateToISOStringDate(date.split(', ')[0]);
        this.toDateFormatted = this.formatOnlyDate(this.toDate);
      } else {        
        this.toDate = null;
        this.toDateFormatted = null;
      }
    }, 
    onCancel() {
      if (this.dataHasChanged) {
        this.$emit('refresh');
        this.showDialog = false;
      } else {
        this.showDialog = false;
      }
    },
  },
  watch: {
    fromDate() {
      this.updateFormattedDate();
    },
    toDate() {
      this.updateFormattedDate();
    },
    plannedEndDate() {
      this.updateFormattedDate();
    },
    plannedStartDate() {
      this.updateFormattedDate();
    }
  }
}
</script>